import {
  ACCEPT_OPTIONS,
  type PendingAsk,
  REVIEW_COMMENT_MAX,
  type ReviewInboxSource,
  type ReviewRequest,
} from "./review-inbox";

/** Error as the reviews router throws it: one code per case, German message (contract, "Fehler"). */
function reviewError(code: "NOT_FOUND" | "CONFLICT" | "BAD_REQUEST", message: string) {
  return Object.assign(new Error(message), { code });
}

/** A bubble of a mock chat; same shape as the live chat rows. */
export type MockThreadRow = { key: string; role: "user" | "bot"; text: string };

const threadKey = (item: { botId: string; sessionId: string | null }) =>
  `${item.botId}:${item.sessionId ?? ""}`;

/** Test chats behind the mock cards (development only); questions sent from the page join them. */
const mockThreads = new Map<string, MockThreadRow[]>([
  [
    "cmvevqza2009h11nycwj6d23s:cmvses0000001aaaaaaaaaaaa",
    [
      { key: "m1", role: "user", text: "Warum steht „Wartet im Chat“ unter „Offen“?" },
      {
        key: "m2",
        role: "bot",
        text: "Offenes kommt zuerst, weil du dort entscheiden musst. Bei „Wartet im Chat“ ist der Bot am Zug.",
      },
    ],
  ],
]);

/** The mock chat of a card, oldest first. */
export function mockThreadRows(item: { botId: string; sessionId: string | null }): MockThreadRow[] {
  return [...(mockThreads.get(threadKey(item)) ?? [])];
}

/**
 * In-memory stand-in for the reviews router with the data of Heron's mock
 * shared/rakazo/pruef-eingang-mock.json and the rules of the contract (answer checks, CONFLICT,
 * NOT_FOUND, order as the server keeps it). Questions join the card's mock chat with a short
 * reply. Test data only; lives as long as the page.
 */
export function createMockReviewSource(): ReviewInboxSource {
  const card = (fields: Partial<ReviewRequest> & Pick<ReviewRequest, "id" | "title">) =>
    ({
      botId: "cmvevqza2009h11nycwj6d23s",
      botName: "Phidias",
      threadId: "cmvthr0000001aaaaaaaaaaaa",
      sessionId: null,
      ticketRef: null,
      parentId: null,
      summary: "",
      kind: "entscheidung",
      options: [],
      previews: [],
      status: "offen",
      position: 1,
      answer: null,
      createdAt: "2026-11-01T20:10:00.000+00:00",
      updatedAt: "2026-11-01T20:10:00.000+00:00",
      ...fields,
    }) satisfies ReviewRequest;

  let items: ReviewRequest[] = [
    card({
      id: "cmvrev0000001aaaaaaaaaaaa",
      sessionId: "cmvses0000001aaaaaaaaaaaa",
      ticketRef: "RAK-426",
      title: "Seitenleiste mit Eintrag „Überprüfung“ abnehmen",
      summary:
        "Neuer Eintrag unter **Artefakte** mit Badge.\n\n- Badge zählt offene Karten und offene Rückfragen\n- Ziehen zum Sortieren\n\nScreenshot 390 px anbei.",
      kind: "abnahme",
      options: [...ACCEPT_OPTIONS],
      previews: [
        {
          kind: "artifact",
          artifactId: "cmvart0000001aaaaaaaaaaaa",
          name: "seitenleiste-390.png",
          mimeType: "image/png",
          live: false,
        },
        {
          kind: "artifact",
          artifactId: "cmvart0000002aaaaaaaaaaaa",
          name: "Vorschau Überprüfung",
          mimeType: "text/html",
          live: true,
        },
      ],
    }),
    card({
      id: "cmvrev0000002aaaaaaaaaaaa",
      botId: "cmve76btt007811pii5kf8ycw",
      botName: "Markus CTO",
      threadId: "cmvthr0000002aaaaaaaaaaaa",
      title: "Ausrollen jhl-robotics PR #21?",
      position: 2,
      summary: "Sokrates und ich haben abgenommen. Ausrollen braucht deine Zustimmung.",
      options: ["Jetzt ausrollen", "Morgen früh", "Nicht ausrollen"],
      previews: [
        { kind: "link", url: "https://github.com/jhltwietmeyer-ux/jhl-robotics/pull/21" },
        {
          kind: "artifact",
          artifactId: "cmvart0000003aaaaaaaaaaaa",
          name: "Pruefbericht.csv",
          mimeType: "text/csv",
          live: false,
        },
      ],
      createdAt: "2026-11-01T19:00:00.000+00:00",
      updatedAt: "2026-11-01T19:00:00.000+00:00",
    }),
    card({
      id: "cmvrev0000003aaaaaaaaaaaa",
      botId: "cmvskkc2wee7n594uw4d7eua9",
      botName: "Solon",
      threadId: "cmvthr0000003aaaaaaaaaaaa",
      sessionId: "cmvses0000003aaaaaaaaaaaa",
      ticketRef: "RAK-410",
      title: "Anmerkungen zum Vertragsentwurf",
      position: 3,
      summary: "Bitte Abschnitt 4 (Haftung) ansehen und frei kommentieren.",
      kind: "kommentar",
      previews: [
        {
          kind: "artifact",
          artifactId: "cmvart0000004aaaaaaaaaaaa",
          name: "Vertrag.md",
          mimeType: "text/markdown",
          live: false,
        },
      ],
      status: "zurueckgestellt",
      createdAt: "2026-11-01T15:30:00.000+00:00",
      updatedAt: "2026-11-01T18:00:00.000+00:00",
    }),
  ];
  const asks: PendingAsk[] = [
    {
      botId: "cmveci68f005f11n474do6z54",
      botName: "Heron Backend",
      threadId: "cmvthr0000004aaaaaaaaaaaa",
      sessionId: "cmvses0000004aaaaaaaaaaaa",
      messageId: "cmvmsg0000001aaaaaaaaaaaa",
      text: "Freigabe: Postgres-Zugang für Tests speichern?",
      createdAt: "2026-11-01T20:30:00.000+00:00",
    },
  ];

  const wait = () => new Promise((resolve) => setTimeout(resolve, 120));
  const find = (id: string) => {
    const item = items.find((entry) => entry.id === id);
    if (!item) throw reviewError("NOT_FOUND", "Prüfauftrag nicht gefunden.");
    return item;
  };
  const pendingCard = (id: string) => {
    const item = find(id);
    if (item.status === "beantwortet" || item.status === "zurueckgezogen")
      throw reviewError("CONFLICT", "Prüfauftrag ist schon beantwortet oder zurückgezogen.");
    return item;
  };
  const update = (id: string, change: Partial<ReviewRequest>) => {
    const next = { ...find(id), ...change, updatedAt: new Date().toISOString() };
    items = items.map((entry) => (entry.id === id ? next : entry));
    return { ...next };
  };

  return {
    async list() {
      await wait();
      return {
        items: items
          .filter((item) => item.status === "offen" || item.status === "zurueckgestellt")
          .map((item) => ({ ...item })),
        asks: asks.map((ask) => ({ ...ask })),
      };
    },
    async answer(id, input) {
      await wait();
      const item = pendingCard(id);
      const option = input.option ?? null;
      const comment = input.comment ?? null;
      if (item.kind === "kommentar" ? !comment : !option || !item.options.includes(option))
        throw reviewError("BAD_REQUEST", "Diese Option gibt es für den Prüfauftrag nicht.");
      if (comment && comment.length > REVIEW_COMMENT_MAX)
        throw reviewError("BAD_REQUEST", "Der Kommentar ist zu lang.");
      return update(id, {
        status: "beantwortet",
        answer: { option, comment, answeredAt: new Date().toISOString() },
      });
    },
    async defer(id) {
      await wait();
      pendingCard(id);
      return update(id, { status: "zurueckgestellt" });
    },
    async reopen(id) {
      await wait();
      pendingCard(id);
      return update(id, { status: "offen" });
    },
    async reorder(id, beforeId) {
      await wait();
      const moved = pendingCard(id);
      const rest = items.filter((item) => item.id !== id);
      const at = beforeId === null ? -1 : rest.findIndex((item) => item.id === beforeId);
      items = at === -1 ? [...rest, moved] : [...rest.slice(0, at), moved, ...rest.slice(at)];
      return { ...moved };
    },
    async count() {
      await wait();
      return items.filter((item) => item.status === "offen").length + asks.length;
    },
    async ask(item, text) {
      await wait();
      const rows = mockThreads.get(threadKey(item)) ?? [];
      mockThreads.set(threadKey(item), [
        ...rows,
        { key: `q${rows.length}`, role: "user", text },
        { key: `a${rows.length}`, role: "bot", text: "Danke, ich sehe es mir an." },
      ]);
    },
  };
}
